/* Single source of truth for the app's navigable destinations.
 *
 * Both the nav leaf menus (persons/reports/scheduling/congregation) and the home-page
 * search read from this list, so routes + labels + permissions live in ONE place
 * (PR #2572 review: "same labels and a single source of truth"). The nav renders it via
 * NavMenu; home derives its search index from the `searchable` entries.
 *
 * `show` encodes each item's real visibility (permission combos, admin/CO/runtime flags);
 * `areaVisible` encodes the section-level menu gate so home search also respects it. */
import {
  BarChartFill,
  CalendarRange,
  CalendarWeek,
  ClipboardData,
  Diagram3,
  Droplet,
  Easel,
  ExclamationCircle,
  FolderSymlink,
  GeoAlt,
  GraphUp,
  Icon,
  ListUl,
  Map as MapIcon,
  Mic,
  PeopleFill,
  PersonBadge,
  PersonCheck,
  PersonPlus,
  PersonVcard,
  PinAngle,
  Speaker,
  Telephone,
} from "react-bootstrap-icons";
import HourglassGlobals from "../../helpers/globals";
import { hasPermission } from "../../helpers/hasPermission";
import { TranslationKey } from "../../helpers/i18n";
import { Permission } from "../../types/permission";
import { UserStatus } from "../../types/user";

export type NavArea = "people" | "reports" | "scheduling" | "congregation" | "smpw";

/** runtime flags a few items depend on that aren't derivable from globals (the leaf supplies them) */
export type NavRuntime = { haveLocation?: boolean; hasE2eKey?: boolean };

export type NavItem = {
  id: string;
  area: NavArea;
  /** divider group — a divider is drawn between visible groups within an area */
  group: string;
  labelKey: TranslationKey;
  /** internal route (a leaf link) — mutually exclusive with `action` */
  path?: string;
  external?: boolean;
  /** non-navigation item (opens a modal etc.); the leaf provides the handler via NavMenu's onAction */
  action?: string;
  /** opt OUT of the home-page search index — navigable (path) items are searchable by default */
  searchable?: boolean;
  /** override the area-derived home-search category label */
  searchAreaKey?: TranslationKey;
  /** override the area-derived home-search icon */
  icon?: Icon;
  /** per-item visibility; omitted means always visible within its (gated) area */
  show?: (rt: NavRuntime) => boolean;
};

/* ---- small predicate helpers (mirror the current nav files exactly) ---- */
const perm = (p: Permission) => hasPermission(p);
const isAdmin = () => HourglassGlobals.isAdmin();
const isCO = () => HourglassGlobals.authUser.status === UserStatus.CO;

/** section-level menu gates — copied from main-nav.tsx so home search matches the sidebar */
export function areaVisible(area: NavArea): boolean {
  switch (area) {
    case "people":
    case "congregation":
      return canViewCongMenus();
    case "reports":
      return perm(Permission.ViewSummary) && canViewCongMenus();
    case "scheduling":
      return canViewSchedulesMenu();
    case "smpw":
      return canViewSMPWMenu();
  }
}
function canViewSchedulesMenu(): boolean {
  return (
    perm(Permission.ViewSchedules) ||
    perm(Permission.UpdateMaintenanceSchedules) ||
    perm(Permission.UpdateCustomAssignments) ||
    perm(Permission.ViewWeekendSpeakers)
  );
}
function canViewSMPWMenu(): boolean {
  return !!HourglassGlobals.authUser.smpw_root_zone_id;
}
function canViewCongMenus(): boolean {
  if (perm(Permission.ViewWeekendSpeakers) && HourglassGlobals.permissions.size === 1) return false;
  return !!HourglassGlobals.cong;
}

/** sub-section header rendered for a group (when that group first has a visible item) */
export const NAV_GROUP_HEADERS: Partial<Record<string, TranslationKey>> = {
  "reports.monthly": "nav.reports.monthly.0",
  "reports.averages": "nav.reports.averages.0",
  "people.contacts": "nav.publishers.contact-lists",
};

export const NAV_ITEMS: NavItem[] = [
  /* ---------------- People (nav "Publishers" section) ---------------- */
  {
    id: "add-user",
    area: "people",
    group: "people.main",
    labelKey: "nav.publishers.add",
    path: "/user/edit/0",
    show: () => perm(Permission.UpdateUsers),
  },
  {
    id: "transfer",
    area: "people",
    group: "people.main",
    labelKey: "nav.publishers.transfer",
    path: "/transfer",
    show: () => perm(Permission.UpdateUsers),
  },
  {
    id: "tags",
    area: "people",
    group: "people.main",
    labelKey: "tags.title",
    path: "/publisher/tags",
    show: () => perm(Permission.UpdateUsers),
  },
  {
    id: "send-reminders",
    area: "people",
    group: "people.main",
    labelKey: "nav.publishers.send-reminders",
    action: "reminders",
    show: () => perm(Permission.SendPush),
  },
  {
    id: "record-cards",
    area: "people",
    group: "people.main",
    labelKey: "nav.publishers.record-cards",
    action: "recordCards",
    show: () => perm(Permission.ViewReportsAll),
  },
  {
    id: "not-publishing",
    area: "people",
    group: "people.main",
    labelKey: "nav.publishers.not-publishing",
    path: "/notpublishing",
    show: () => perm(Permission.ViewUsersNotPublishing),
  },
  {
    id: "publisher-import",
    area: "people",
    group: "people.main",
    labelKey: "nav.congregation.import",
    path: "/publisher/import",
    show: () => perm(Permission.UpdateUsers) && perm(Permission.UpdateReports),
  },
  {
    id: "publisher-map",
    area: "people",
    group: "people.main",
    labelKey: "general.map",
    path: "/publisher/map",
    show: (rt) => !!rt.haveLocation,
  },
  {
    id: "contact-list",
    area: "people",
    group: "people.contacts",
    labelKey: "nav.publishers.contacts.all-publishers",
    path: "/contactList",
    searchable: true,
    icon: PersonVcard,
  },
  {
    id: "emergency-list",
    area: "people",
    group: "people.contacts",
    labelKey: "nav.publishers.contacts.emergency",
    path: "/emergencylist",
    searchable: true,
    icon: Telephone,
    show: () => perm(Permission.ViewEmergencycontacts),
  },

  /* ---------------- Reports ---------------- */
  {
    id: "missing",
    area: "reports",
    group: "reports.top",
    labelKey: "chart.legend.missing",
    path: "/missing",
    searchable: true,
    icon: ExclamationCircle,
  },
  {
    id: "month-summary",
    area: "reports",
    group: "reports.monthly",
    labelKey: "nav.reports.monthly.summary",
    path: "/monthSummary",
    searchable: true,
    icon: CalendarRange,
  },
  {
    id: "month-detail",
    area: "reports",
    group: "reports.monthly",
    labelKey: "nav.reports.monthly.detail",
    path: "/monthDetail",
    searchable: true,
    icon: ListUl,
    show: () => perm(Permission.ViewSummaryDetails) || perm(Permission.ViewSummaryDetailsGroup),
  },
  {
    id: "month-statistics",
    area: "reports",
    group: "reports.monthly",
    labelKey: "nav.reports.monthly.statistics",
    path: "/monthStatistics",
    searchable: true,
    icon: GraphUp,
  },
  {
    id: "cong-avg",
    area: "reports",
    group: "reports.averages",
    labelKey: "list.congregation.title",
    path: "/congAvg",
    searchable: true,
    icon: BarChartFill,
  },
  {
    id: "group-avg",
    area: "reports",
    group: "reports.averages",
    labelKey: "nav.reports.averages.group",
    path: "/groupAvg",
    show: () => perm(Permission.ViewSummaryDetails) || perm(Permission.ViewSummaryDetailsGroup),
  },
  {
    id: "pub-avg",
    area: "reports",
    group: "reports.averages",
    labelKey: "list.publishers.title",
    path: "/pubAvg",
    show: () => perm(Permission.ViewReportsAll),
  },
  {
    id: "rp-summary",
    area: "reports",
    group: "reports.pioneers",
    labelKey: "recordcard.svcyeartotals.regular-pioneers",
    path: "/rpSummary",
    searchable: true,
    icon: PersonCheck,
    show: () => perm(Permission.ViewSummaryRegpio),
  },
  {
    id: "ap-summary",
    area: "reports",
    group: "reports.pioneers",
    labelKey: "recordcard.svcyeartotals.auxiliary-pioneers",
    path: "/apSummary",
    searchable: true,
    icon: PersonPlus,
    show: () => perm(Permission.ViewSummaryDetails),
  },
  {
    id: "cong-analysis",
    area: "reports",
    group: "reports.bottom",
    labelKey: "nav.reports.cong-analysis",
    path: "/congAnalysis",
    searchable: true,
    icon: ClipboardData,
  },
  {
    id: "svc-year-totals",
    area: "reports",
    group: "reports.bottom",
    labelKey: "nav.reports.svcyeartotals",
    path: "/svcYearTotals",
    searchable: true,
    icon: CalendarRange,
  },
  {
    id: "co-reports",
    area: "reports",
    group: "reports.bottom",
    labelKey: "general.circuit-overseer",
    path: "/coReports",
    searchable: true,
    icon: PersonBadge,
    show: () => perm(Permission.ViewReportsAll) && perm(Permission.ViewUsers),
  },

  /* ---------------- Scheduling (home search "Assignments") ---------------- */
  {
    id: "midweek",
    area: "scheduling",
    group: "sched.main",
    labelKey: "conganalysis.attendance.midweek",
    path: "/scheduling/mm",
    searchable: true,
    icon: Easel,
    show: () => perm(Permission.ViewSchedules),
  },
  {
    id: "weekend",
    area: "scheduling",
    group: "sched.main",
    labelKey: "conganalysis.attendance.weekend",
    path: "/scheduling/wm",
    searchable: true,
    icon: Mic,
    show: () => perm(Permission.ViewSchedules) || perm(Permission.ViewWeekendSpeakers),
  },
  {
    id: "av-attendant",
    area: "scheduling",
    group: "sched.main",
    labelKey: "schedules.ava.title",
    path: "/scheduling/avattendant",
    searchable: true,
    icon: Speaker,
    show: () => perm(Permission.ViewSchedules),
  },
  {
    id: "cleaning",
    area: "scheduling",
    group: "sched.main",
    labelKey: "schedules.cleaning.title",
    path: "/scheduling/cleaning",
    searchable: true,
    icon: Droplet,
    show: () => perm(Permission.ViewSchedules),
  },
  {
    id: "fs-meeting",
    area: "scheduling",
    group: "sched.main",
    labelKey: "schedules.field-service.title",
    path: "/scheduling/fsMeeting",
    searchable: true,
    icon: GeoAlt,
    show: () => perm(Permission.ViewSchedules),
  },
  {
    id: "public-witnessing",
    area: "scheduling",
    group: "sched.main",
    labelKey: "schedules.public-witnessing.title",
    path: "/scheduling/publicWitnessing",
    show: () => perm(Permission.ViewSchedules),
  },
  {
    id: "maintenance",
    area: "scheduling",
    group: "sched.main",
    labelKey: "schedules.maintenance.title",
    path: "/scheduling/maintenance",
    show: () => perm(Permission.ViewSchedules) || perm(Permission.UpdateMaintenanceSchedules),
  },
  {
    id: "custom",
    area: "scheduling",
    group: "sched.main",
    labelKey: "general.custom",
    path: "/scheduling/custom",
    searchable: true,
    icon: CalendarWeek,
    show: () => perm(Permission.ViewSchedules) || perm(Permission.UpdateCustomAssignments),
  },
  {
    id: "memorial",
    area: "scheduling",
    group: "sched.events",
    labelKey: "schedules.events.memorial",
    path: "/scheduling/memorial",
    show: () => perm(Permission.ViewSchedules),
  },
  {
    id: "privilege-matrix",
    area: "scheduling",
    group: "sched.admin",
    labelKey: "schedules.privileges.title",
    path: "/scheduling/privilege_matrix",
    show: () => perm(Permission.ViewSchedules) && (perm(Permission.ViewUsers) || perm(Permission.ViewUsersMinimal)),
  },
  {
    id: "absence",
    area: "scheduling",
    group: "sched.admin",
    labelKey: "schedules.absence.title",
    path: "/scheduling/absence",
    show: () => perm(Permission.ViewSchedules),
  },
  {
    id: "events",
    area: "scheduling",
    group: "sched.admin",
    labelKey: "schedules.events.title",
    path: "/scheduling/events",
    show: () => perm(Permission.ViewSchedules),
  },
  {
    id: "sched-settings",
    area: "scheduling",
    group: "sched.admin",
    labelKey: "general.settings",
    path: "/scheduling/settings",
    show: () => perm(Permission.ViewSchedules) && perm(Permission.UpdateCongregation),
  },

  /* ---------------- Congregation ---------------- */
  {
    id: "manage-groups",
    area: "congregation",
    group: "cong.main",
    labelKey: "nav.congregation.fsgroups",
    path: "/manageGroups",
    searchable: true,
    icon: Diagram3,
  },
  {
    id: "manage-lang-groups",
    area: "congregation",
    group: "cong.main",
    labelKey: "schedules.language-groups.title",
    path: "/manageLangGroups",
  },
  {
    id: "attendance",
    area: "congregation",
    group: "cong.main",
    labelKey: "nav.congregation.attendance",
    path: "/attendance",
    show: () => perm(Permission.ViewSummary) || perm(Permission.UpdateAttendance),
  },
  {
    id: "territory",
    area: "congregation",
    group: "cong.main",
    labelKey: "schedules.territory.territory",
    path: "/congregation/territory",
    searchable: true,
    icon: MapIcon,
    show: () => perm(Permission.UpdateTerritory) || perm(Permission.ViewTerritory),
  },
  {
    id: "cong-profile",
    area: "congregation",
    group: "cong.admin",
    labelKey: "nav.congregation.profile-permissions",
    path: "/congProfile",
    searchable: true,
    icon: PersonVcard,
    show: () => perm(Permission.UpdateCongregation) || perm(Permission.ManagePermissions),
  },
  {
    id: "cong-notifications",
    area: "congregation",
    group: "cong.admin",
    labelKey: "general.notifications",
    path: "/congNotifications",
    show: () => perm(Permission.ManagePermissions),
  },
  {
    id: "activity",
    area: "congregation",
    group: "cong.admin",
    labelKey: "nav.activity",
    path: "/activity",
    show: () => isAdmin(),
  },
  {
    id: "cong-import",
    area: "congregation",
    group: "cong.admin",
    labelKey: "nav.congregation.import",
    path: "/import",
    show: () => perm(Permission.UpdateUsers) && perm(Permission.UpdateReports) && perm(Permission.UpdateAttendance),
  },
  {
    id: "cong-export",
    area: "congregation",
    group: "cong.admin",
    labelKey: "nav.congregation.export",
    action: "export",
    show: () =>
      perm(Permission.ViewUsers) &&
      perm(Permission.ViewReportsAll) &&
      perm(Permission.ViewAddresses) &&
      perm(Permission.ViewEmergencycontacts),
  },
  {
    id: "e2e-migration",
    area: "congregation",
    group: "cong.system",
    labelKey: "nav.e2e",
    path: "/e2eMigration",
    show: (rt) => isAdmin() && !!rt.hasE2eKey,
  },
  {
    id: "cong-settings",
    area: "congregation",
    group: "cong.system",
    labelKey: "general.settings",
    path: "/settings",
    show: () => isAdmin(),
  },
  {
    id: "cong-roles",
    area: "congregation",
    group: "cong.system",
    labelKey: "general.responsibilities",
    path: "/congregation/roles",
    searchable: true,
    icon: PersonBadge,
    show: () => perm(Permission.UpdateCongregation),
  },
  {
    id: "infoboard",
    area: "congregation",
    group: "cong.info",
    labelKey: "general.information-board",
    path: "/infoboard",
    searchable: true,
    icon: PinAngle,
  },
  {
    id: "circuit",
    area: "congregation",
    group: "cong.circuit",
    labelKey: "circuit.circuit",
    path: "/circuit",
    show: () => isCO(),
  },

  /* ---------------- SMPW ---------------- */
  { id: "smpw-dashboard", area: "smpw", group: "smpw.main", labelKey: "smpw.my_dashboard", path: "/smpw" },
  {
    id: "smpw-scheduling",
    area: "smpw",
    group: "smpw.main",
    labelKey: "schedules.scheduling",
    path: "/smpw/scheduling",
    show: () => perm(Permission.SMPWAdmin) || perm(Permission.SMPWSchedule),
  },
  {
    id: "smpw-reports",
    area: "smpw",
    group: "smpw.main",
    labelKey: "nav.reports.0",
    path: "/smpw/reports",
    show: () => perm(Permission.SMPWAdmin) || perm(Permission.SMPWReports),
  },
  {
    id: "smpw-admin",
    area: "smpw",
    group: "smpw.main",
    labelKey: "smpw.admin",
    path: "/smpw/admin",
    show: () => perm(Permission.SMPWAdmin) || perm(Permission.SMPWUpdateUsers),
  },

  /* ---------------- Search-only destinations (not leaf items, but searchable) ---------------- */
  {
    id: "persons-landing",
    area: "people",
    group: "_search",
    labelKey: "general.persons",
    path: "/nav/publishers",
    searchable: true,
    searchAreaKey: "general.persons",
    icon: PeopleFill,
  },
  {
    id: "docs",
    area: "congregation",
    group: "_search",
    labelKey: "nav.documents",
    path: "/docs",
    searchable: true,
    icon: FolderSymlink,
  },
];

/** visible nav-leaf items for an area (section gate + item `show`), in declared order. Excludes search-only. */
export function navItemsForArea(area: NavArea, rt: NavRuntime = {}): NavItem[] {
  if (!areaVisible(area)) return [];
  return NAV_ITEMS.filter((i) => i.area === area && i.group !== "_search" && (i.show ? i.show(rt) : true));
}

/** per-area defaults for the home search (category label + icon), used when an item doesn't
 *  set its own searchArea/icon. */
const AREA_SEARCH: Record<NavArea, { searchAreaKey: TranslationKey; icon: Icon }> = {
  people: { searchAreaKey: "general.persons", icon: PeopleFill },
  reports: { searchAreaKey: "nav.reports.0", icon: GraphUp },
  scheduling: { searchAreaKey: "schedules.scheduling", icon: CalendarWeek },
  congregation: { searchAreaKey: "list.congregation.title", icon: Diagram3 },
  smpw: { searchAreaKey: "smpw.smpw", icon: PeopleFill },
};

export type SearchDestination = {
  labelKey: TranslationKey;
  path: string;
  external?: boolean;
  searchAreaKey: TranslationKey;
  icon: Icon;
};

/** Every navigable (path-bearing) destination the current user may open, for the unified search.
 *  Search is opt-OUT (`searchable: false`) rather than opt-in, so adding a page to NAV_ITEMS makes
 *  it findable automatically — one registry entry feeds both nav and search (PR #2572 review). */
export function searchableDestinations(): SearchDestination[] {
  return NAV_ITEMS.filter(
    (i) => !!i.path && i.searchable !== false && areaVisible(i.area) && (i.show ? i.show({}) : true),
  ).map((i) => ({
    labelKey: i.labelKey,
    path: i.path!,
    external: i.external,
    searchAreaKey: i.searchAreaKey ?? AREA_SEARCH[i.area].searchAreaKey,
    icon: i.icon ?? AREA_SEARCH[i.area].icon,
  }));
}
